import * as yup from 'yup';
import {
    AGE_BOUNDS,
    CHILD_AGE_BOUNDS,
    emailField,
    integerIn,
    NAME_MAX,
    PAIN_LEVEL_BOUNDS,
    predicateRule,
    requiredText,
    TranslateFn,
} from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';

/**
 * One schema for the whole intake rather than one per step, so the wizard can validate a
 * slice of it on each Continue and the same rules apply on the final submit. The steps
 * name their own fields; see STEP_FIELDS in the wizard.
 *
 * Almost everything is optional. A first session is often two lines and nothing else,
 * and a form that will not save until every box is filled is a form that gets filled
 * with dashes. What is *not* optional is the email address -- the account cannot exist
 * without it -- the treatment consent, and the signature that goes with it.
 *
 * `requiresGdprConsent` is what the data-processing agreement hangs on, and it has to be
 * a parameter rather than a rule the wizard skips. `useStepValidity` filters failures by
 * which step owns them, so an agreement nobody is showing would be ignored on the way
 * through -- but `handleSubmit` runs the resolver over the whole object, so an unchecked
 * `.oneOf([true])` would leave Save dead with nothing on screen to explain why. Made
 * false, the rule is not there at all rather than there and unreachable.
 */
export const createEvaluationSchema = (translate: TranslateFn, requiresGdprConsent: boolean) =>
    yup.object({
    emailAddress: emailField(translate),
    firstName: requiredText(translate, ResourcesIds.FIRST_NAME_REQUIRED, NAME_MAX),
    lastName: requiredText(translate, ResourcesIds.LAST_NAME_REQUIRED, NAME_MAX),

    phoneNumber: yup.string().defined(),
    occupation: yup.string().defined(),

    // Held as strings because that is what an input gives back; the model converts them
    // once on submit. AGE_BOUNDS is also what the input's own spinner reads, so the two
    // cannot say different things.
    age: integerIn(AGE_BOUNDS, translate, ResourcesIds.EVALUATION_AGE_INVALID),
    sex: yup.string().defined(),
    evaluatedOn: yup.string().defined(),
    reasonForVisit: yup.string().defined(),
    investigations: yup.string().defined(),
    painLevel: integerIn(PAIN_LEVEL_BOUNDS, translate, ResourcesIds.EVALUATION_PAIN_LEVEL_INVALID),
    painCharacter: yup.string().defined(),
    medication: yup.string().defined(),
    injuryHistory: yup.string().defined(),
    notes: yup.string().defined(),

    consentCapacity: yup.string().oneOf(['PATIENT', 'LEGAL_REPRESENTATIVE'] as const).defined(),

    // Required exactly when somebody is signing for a child, which is a rule about one
    // field given another and so cannot live on the field alone. Nought is a valid answer
    // here and not for `age`: an infant brought in by a parent is real.
    childAge: yup.string().defined()
        .when('consentCapacity', {
            is: 'LEGAL_REPRESENTATIVE',
            then: () => integerIn(CHILD_AGE_BOUNDS, translate,
                ResourcesIds.EVALUATION_CHILD_AGE_INVALID,
                { required: true, requiredKey: ResourcesIds.EVALUATION_CHILD_AGE_REQUIRED }),
        }),

    patientAddress: yup.string().defined(),
    idSeries: yup.string().defined(),
    idNumber: yup.string().defined(),
    referringDoctor: yup.string().defined(),

    // The API refuses this too. Checked here so the therapist finds out on the step they
    // are on rather than after filling in the rest.
    treatmentConsentGiven: yup.boolean().defined()
        .oneOf([true], translate(ResourcesIds.EVALUATION_TREATMENT_CONSENT_REQUIRED)),

    // Both belong to the treatment consent's signature block, so both are wanted on every
    // sheet -- including a returning patient's, who is never shown the agreement.
    signedName: requiredText(translate,
        ResourcesIds.EVALUATION_SIGNED_NAME_REQUIRED, NAME_MAX),

    // The signature proper, and the printed name above is not a substitute for it.
    // Required like the consent and for the same reason: the API renders it into the
    // document and keeps no copy, so an intake submitted unsigned cannot be signed
    // afterwards. A non-empty value is the whole rule -- the pad only ever hands over
    // base64 it produced itself.
    signature: predicateRule('signed', (value) => value !== '',
        translate(ResourcesIds.EVALUATION_SIGNATURE_REQUIRED)),

    // Asked once per patient per clinic. Where one is already on file the field is still
    // part of the form -- it is sent, as false -- but nothing is required of it, because
    // nobody was shown anything to agree to.
    gdprConsentGiven: requiresGdprConsent
        ? yup.boolean().defined()
            .oneOf([true], translate(ResourcesIds.EVALUATION_GDPR_CONSENT_REQUIRED))
        : yup.boolean().defined(),
});
